<template>
  <div>
    <MarketingHero
      title-keypath="hero.title"
      :highlighted-title="categoryLabel"
      :subtitle="t(`events.categories.${eventType}.shortDescription`)"
    >
      <template #pre-title>
        <TBreadcrumb :items="breadcrumbs" />
      </template>

      <p class="pre-wrap whitespace-pre-line">
        {{ t(`events.categories.${eventType}.introParagraph`) }}
      </p>

      <template #cta>
        <TButton
          variant="highlight"
          size="lg"
          @click="navigateToRvSearch({ Delivery: true, FestivalFriendly: true })"
        >
          {{ t('viewDeliverableRVs') }}
        </TButton>
      </template>

      <template #image>
        <TImage
          :src="categoryHeroImage"
          :alt="t('heroImageAlt', { category: categoryLabel })"
          class="aspect-3/2 object-cover rounded-xl"
        />
      </template>
    </MarketingHero>

    <div class="py-12 lg:py-16 space-y-16">
      <TContainer
        v-if="!data?.Data?.length"
        size="xl"
      >
        <div class="bg-gray-50 p-8 rounded-xl text-center">
          <THeading
            :level="3"
            tag="h2"
            class="mb-6 justify-center"
          >
            {{ eventsListHeading }}
          </THeading>

          <p class="text-gray-500 ">
            {{ t('empty') }}
          </p>
        </div>
      </TContainer>
      <TContainer v-else>
        <THeading
          :level="3"
          tag="h2"
        >
          {{ eventsListHeading }}
        </THeading>

        <div class="mt-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          <TMarketingCard
            v-for="event in formattedEvents"
            :key="event.title"
            :title="event.title"
            :subtitle="event.description"
            :callout="event.callout"
            :image="event.image"
            :to="event.url"
            clamp-subtitle
          />
        </div>
      </TContainer>

      <TContainer
        v-if="otherCategories.length"
        size="xl"
        class="space-y-4"
      >
        <THeading
          :level="3"
          tag="h2"
        >
          {{ t('otherCategoriesTitle') }}
        </THeading>

        <div class="flex flex-wrap gap-4">
          <NuxtLink
            v-for="cat in otherCategories"
            :key="cat.type"
            :to="cat.to"
            class="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            {{ cat.label }}
          </NuxtLink>
        </div>
      </TContainer>

      <TContainer size="xl">
        <RvRentalsEventFriendly />

        <div class="flex justify-center">
          <TButton
            variant="highlight"
            @click="navigateToRvSearch({ Delivery: true, FestivalFriendly: true })"
          >
            {{ t('viewDeliverableRVs') }}
          </TButton>
        </div>
      </TContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EVENT_CATEGORY_IMAGES, EVENT_CATEGORY_SLUGS, EVENT_TYPE_BY_SLUG } from '~/constants/events-categories'
import { ROUTE_BASE_NAME } from '~/constants'

definePageMeta({
  layout: 'static',

  i18n: {
    paths: {
      en: '/events/[eventCategory]',
      fr: '/evenements/[eventCategory]',
    },
  },
})

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { locale, t } = useI18n()
const localePath = useLocalePath()
const { getStaticImage, presets } = useImage()
const { navigateToRvSearch } = useSearch()

const rawParam = route.params.eventCategory
const categorySlug = computed(() => String(Array.isArray(rawParam) ? rawParam[0] : rawParam ?? ''))

const eventType = computed(() => EVENT_TYPE_BY_SLUG[locale.value][categorySlug.value])

if (!eventType.value) {
  throw createError({
    status: 404,
    statusText: 'Event category not found',
  })
}

const { data } = await useRentals('/api/events', {
  query: {
    EventType: eventType.value,
    LanguageCode: locale.value,
    PageNumber: 1,
    PageSize: 100,
  },
})

const categoryName = computed(() => t(`events.categories.${eventType.value}.name`))
const categoryLabel = computed(() => t(`events.categories.${eventType.value}.label`))

const eventsListHeading = computed(() =>
  t('eventsListHeading', { category: categoryLabel.value }),
)

const metaTitle = computed(() => t(`events.categories.${eventType.value}.metaTitle`))

const metaDescription = computed(() => t(`events.categories.${eventType.value}.metaDescription`))

const categoryHeroImage = computed(() => {
  if (!eventType.value) {
    return ''
  }

  return getStaticImage({
    path: EVENT_CATEGORY_IMAGES[eventType.value],
    preset: presets.LANDSCAPE_MD,
  }) ?? ''
})

const categoryOgImage = computed(() => {
  if (!eventType.value) {
    return ''
  }

  return getStaticImage({
    path: EVENT_CATEGORY_IMAGES[eventType.value],
    preset: presets.OG,
  }) ?? ''
})

const formattedEvents = computed(() =>
  (data.value?.Data ?? []).map((event) => {
    const rawImagePath = event?.Images?.[0]?.Path

    return {
      title: event?.Name ?? '',
      description: event?.Description ?? '',
      image: {
        src: getStaticImage({ path: rawImagePath, preset: presets.SQUARE_LG }) ?? '',
        alt: event?.Name ?? '',
      },
      url: event?.Slug ? localePath({ name: ROUTE_BASE_NAME.rv_rentals_event, params: { eventSlug: event?.Slug } }) : undefined,
      callout: [event?.City, event?.Region].filter(Boolean).join(', '),
    }
  }),
)

const otherCategories = computed(() => {
  return EVENT_CATEGORY_SLUGS
    .filter(({ type }) => type !== eventType.value)
    .map(({ type, slug }) => ({
      type,
      label: t(`events.categories.${type}.name`),
      to: localePath({
        name: ROUTE_BASE_NAME.events_eventCategory,
        params: { eventCategory: slug[locale.value] },
      }),
    }))
})

const breadcrumbs = computed(() => [
  {
    text: t('events.title'),
    to: localePath({ name: 'events' }),
  },
  {
    text: categoryName.value,
    to: localePath({
      name: ROUTE_BASE_NAME.events_eventCategory,
      params: { eventCategory: categorySlug.value },
    }),
  },
])

useSeoHeadTags({
  title: metaTitle,
  description: metaDescription,
  image: categoryOgImage,
})

const setI18nParams = useSetI18nParams()

const eventCategorySlugs = computed(() => {
  return EVENT_CATEGORY_SLUGS.find(({ slug }) => slug[locale.value] === categorySlug.value)?.slug
})

setI18nParams({
  en: eventCategorySlugs.value?.en ? { eventCategory: eventCategorySlugs.value.en } : false,
  fr: eventCategorySlugs.value?.fr ? { eventCategory: eventCategorySlugs.value.fr } : false,
})

onBeforeRouteLeave(() => {
  setI18nParams({})
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: 'index' }),
      },
      ...breadcrumbs.value,
    ].map((breadcrumb) => ({
      name: breadcrumb.text,
      item: breadcrumb.to,
    })),
  }),

  defineWebPage({
    '@type': 'CollectionPage',
    'mainEntity': {
      '@type': 'ItemList',
      'itemListElement': formattedEvents.value?.length
        ? formattedEvents.value.filter((e) => Boolean(e.url)).map((e, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'item': {
              '@type': 'Event',
              'name': e.title,
              'url': `${runtimeConfig.public.baseURL}${e.url}`,
            },
          }))
        : undefined,
    },
  }),
])
</script>

<i18n src="~~/i18n/locales/common/events.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "hero": {
      "title": "RV Rentals for {highlight}"
    },
    "pageTitle": "RV-friendly {category}",
    "eventsListHeading": "Upcoming {category} to explore with an RV",
    "heroImageAlt": "{category} — RV travel and event planning on RVezy",
    "empty": "No events in this category right now. Try another category or check back later.",
    "otherCategoriesTitle": "More RV-friendly event categories",
    "viewDeliverableRVs": "View deliverable RVs"
  },
  "fr": {
    "hero": {
      "title": "Locations VR pour {highlight}"
    },
    "pageTitle": "Événements acceptant les VR — {category}",
    "eventsListHeading": "{category} à venir — explorer en VR",
    "heroImageAlt": "{category} — voyage en VR et événements sur RVezy",
    "empty": "Aucun événement dans cette catégorie pour le moment. Essayez une autre catégorie ou revenez plus tard.",
    "otherCategoriesTitle": "Autres catégories d’événements pour VR",
    "viewDeliverableRVs": "Voir les VRs livrables"
  }
}
</i18n>
